import type { FC } from 'react';
import React from 'react';
import styles from './searchBar.module.scss';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon } from '@phosphor-icons/react/dist/csr/MagnifyingGlass';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';
import { SparkleIcon } from '@phosphor-icons/react/dist/csr/Sparkle';
import IconButton from '@mui/material/IconButton';

export type SearchInputProps = {
  query: string;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: (e: React.MouseEvent) => void;
  onSearchClick: (e: React.MouseEvent) => void;
  onAskAgent: () => void;
  showAskAgentAction: boolean;
  isAskAgentLoading: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  searchRef: React.RefObject<HTMLDivElement | null>;
  clearRef: React.RefObject<HTMLDivElement | null>;
};

const SearchInput: FC<SearchInputProps> = ({
  query,
  onSearch,
  onClear,
  onSearchClick,
  onAskAgent,
  showAskAgentAction,
  isAskAgentLoading,
  searchRef,
  inputRef,
  clearRef,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'searchBar' });

  const { control, searchIcon, close, askAgentIcon } = styles;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onAskAgent();
  };

  const handleAskAgent = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAskAgent();
  };

  return (
    <div className={control}>
      <div ref={searchRef} className={searchIcon} data-testid="search-icon" onClick={onSearchClick}>
        <MagnifyingGlassIcon size={18} />
        <input
          id="search-input"
          ref={inputRef}
          autoComplete="off"
          data-testid="search-input"
          placeholder={t('placeholder')}
          type="text"
          value={query}
          name="searchTerm"
          onChange={onSearch}
          onKeyDown={handleKeyDown}
          autoFocus={false}
          tabIndex={-1}
        />
      </div>
      {query ? (
        <>
          {showAskAgentAction ? (
            <IconButton
              className={askAgentIcon}
              onClick={handleAskAgent}
              disabled={isAskAgentLoading}
              aria-label={t('askAgent')}
            >
              <SparkleIcon size={18} />
            </IconButton>
          ) : null}
          <div ref={clearRef} className={close} data-testid="close-icon" onClick={onClear}>
            <XIcon size={18} />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default SearchInput;
